'use client'

import Link from 'next/link'
import { useContext, useEffect, useState } from 'react'

import styled from 'styled-components'

import { GetUserInfoResponse } from '../../../api/generated'
import { colors } from '../../../constants/colors'
import UserStorage from '../../../storage/UserStorage/UserStorage'
import { ReferralCode } from './ReferralCode'
import { loadMainMenuData } from './loadMainMenuData'

const Menu = styled.nav`
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
  width: 100%;
`

const MenuLink = styled(Link)`
  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 12px 30px;
  border-radius: 10px;
  background: ${colors.lightGray};
  color: #000;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  text-decoration: none;

  img {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    object-fit: contain;
    padding: 1.25px;

    &[src$='profile.svg'],
    &[src$='blog.svg'] {
      padding: 1.25px 3.25px;
    }
    &[src$='cart.svg'] {
      padding: 2.25px 3.09px 2.25px 1.25px;
    }
    &[src$='orders.svg'] {
      padding: 1.25px 2.25px;
    }
    &[src$='home.svg'],
    &[src$='chat.svg'],
    &[src$='add.svg'] {
      padding: 0;
    }
  }
`

const AddProductLink = styled(MenuLink)`
  flex-shrink: 0;
  margin-bottom: 24px;
  justify-content: center;
  min-height: 50px;
  padding: 12px 10px;
  background: ${colors.primary};
  color: ${colors.white};
  font-weight: 600;
`

const OrdersLink = styled(MenuLink)`
  padding-right: 12px;
`

const Counter = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  margin-left: auto;
  border-radius: 12px;
  background-color: ${colors.primary};
  color: ${colors.white};
  font-size: 16px;
`

const MenuLayout = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-height: 100%;
  padding-top: 82px;
`

const ReferralWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  margin-bottom: 15px;
`

const LogoutButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  min-height: 50px;
  padding: 15px 0;
  border: 0;
  background: transparent;
  color: ${colors.red};
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;

  img {
    width: 20px;
    height: 20px;
    padding: 0.92px;
  }
`

export const MainMenuWindow = ({ handleModalClose }: { handleModalClose: () => void }) => {
  const store = useContext(UserStorage)
  const [userInfo, setUserInfo] = useState<GetUserInfoResponse>()
  const [referralCode, setReferralCode] = useState<string>()

  useEffect(() => {
    let active = true

    if (!store.isAuth || !store.userId) return

    loadMainMenuData(store.userId)
      .then(data => {
        if (!active) return

        setUserInfo(data.userInfo)
        setReferralCode(data.referralCode)
      })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [store.isAuth, store.userId])

  return (
    <MenuLayout data-main-menu>
      <Menu>
        {!store?.userId && (
          <MenuLink href={'/login'} onClick={handleModalClose}>
            <img alt={''} src={'/images/main-menu/profile.svg'} />
            Войти
          </MenuLink>
        )}
        <MenuLink href={'/profile'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/profile.svg'} />
          Личный кабинет
        </MenuLink>
        <MenuLink href={'/'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/home.svg'} />
          Главная
        </MenuLink>
        <MenuLink href={'/orders'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/cart.svg'} />
          Корзина
        </MenuLink>
        <OrdersLink href={'/messages?activeTab=1'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/orders.svg'} />
          Новые заказы
          {userInfo && <Counter>{userInfo.newRentsAsSellerCount}</Counter>}
        </OrdersLink>
        <MenuLink href={'/messages'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/chat.svg'} />
          Чаты
        </MenuLink>
        <MenuLink href={'/blog'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/blog.svg'} />
          Блог
        </MenuLink>
        <MenuLink href={'/profile/personal-data'} onClick={handleModalClose}>
          <img alt={''} src={'/images/main-menu/settings.svg'} />
          Настройки
        </MenuLink>
        <MenuLink
          href={'https://t.me/Allsharing_support'}
          onClick={handleModalClose}
          target={'_blank'}
        >
          <img alt={''} src={'/images/main-menu/help.svg'} />
          Помощь
        </MenuLink>
      </Menu>
      <AddProductLink href={'/add'} onClick={handleModalClose}>
        <img alt={''} src={'/images/main-menu/add.svg'} />
        Разместить объявление
      </AddProductLink>
      {store?.userId && (
        <ReferralWrapper>
          <ReferralCode referralCode={referralCode} />
        </ReferralWrapper>
      )}
      {store.isAuth && (
        <LogoutButton
          onClick={() => {
            handleModalClose()
            store.logout()
          }}
          type={'button'}
        >
          <img alt={''} src={'/images/main-menu/logout.svg'} />
          <span>Выйти из аккаунта</span>
        </LogoutButton>
      )}
    </MenuLayout>
  )
}
